import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Separator,
  SimpleGrid,
  Table,
  Text,
} from '@chakra-ui/react';
import { api, DeviceStatus, DeviceSystem } from '../api/api';
import { useConfirmation, useNotification } from '../context/NotificationContext';
import { RefreshIcon } from './Icons';
import { useNavigate } from 'react-router-dom';

function timeAgo(dateString: string | undefined): string {
  if (!dateString) return 'Never';
  
  // Parse the UTC timestamp
  const dateUtc = new Date(dateString);
  
  // Get current time in UTC
  const nowUtc = new Date();
  
  // Calculate difference in milliseconds
  const diffMs = nowUtc.getTime() - dateUtc.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  
  if (diffSec < 0) return 'Just now'; // Handle future timestamps
  if (diffSec < 60) return `${diffSec} sec${diffSec !== 1 ? 's' : ''} ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin !== 1 ? 's' : ''} ago`;
  const diffHour = Math.floor(diffMin / 60);
  const remainingMin = diffMin % 60;
  if (diffHour < 24) {
    if (remainingMin === 0) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ${remainingMin} min ago`;
  }
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
  const diffYear = Math.floor(diffMonth / 12);
  return `${diffYear} year${diffYear !== 1 ? 's' : ''} ago`;
}

export default function Dashboard() {
  const [recentSystems, setRecentSystems] = useState<DeviceSystem[]>([]);
  const [expiringSystems, setExpiringSystems] = useState<DeviceSystem[]>([]);
  const [latestStatus, setLatestStatus] = useState<Record<string, DeviceStatus>>({});
  const [clearHistoryLoading, setClearHistoryLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const { confirm } = useConfirmation();
  const { notify } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    api.systems.recent().then(setRecentSystems);
    api.systems.expiring().then(setExpiringSystems);
    api.systemStatus.latest().then(statuses => {
      const map: Record<string, DeviceStatus> = {};
      statuses.forEach(status => {
        if (status) map[status.machineKey] = status;
      });
      setLatestStatus(map);
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      api.systemStatus.latest().then(statuses => {
        const map: Record<string, DeviceStatus> = {};
        statuses.forEach(status => {
          if (status) map[status.machineKey] = status;
        });
        setLatestStatus(map);
      });
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      const statuses = await api.systemStatus.latest();
      const map: Record<string, DeviceStatus> = {};
      statuses.forEach(status => {
        if (status) map[status.machineKey] = status;
      });
      setLatestStatus(map);
      notify({ type: 'success', message: 'Status refreshed successfully.' });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || 'Failed to refresh status';
      notify({ type: 'error', message: errorMessage });
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleClearHistory = () => {
    confirm({
      title: 'Clear Status History',
      message: 'This will keep only the most recent status per machine and remove all older history. Continue?',
      confirmText: 'Clear History',
      cancelText: 'Cancel',
      confirmColorScheme: 'red',
      onConfirm: async () => {
        setClearHistoryLoading(true);
        try {
          const res = await api.systemStatus.clearHistory();
          notify({ type: 'success', message: `History cleared. Deleted ${res.deleted} record(s), kept ${res.kept}.` });
          api.systemStatus.latest().then(statuses => {
            const map: Record<string, DeviceStatus> = {};
            statuses.forEach(status => {
              if (status) map[status.machineKey] = status;
            });
            setLatestStatus(map);
          });
        } catch (err: any) {
          const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || 'Failed to clear history';
          notify({ type: 'error', message: errorMessage });
        } finally {
          setClearHistoryLoading(false);
        }
      }
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={8}>
      <Heading size="lg">Dashboard Overview</Heading>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="white" overflow="auto" maxH="400px">
          <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100" position="sticky" top="0" bg="white" zIndex={1}>
            <Heading size="md">Recently Added Systems</Heading>
          </Box>
          <Box px={2} py={2} overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader whiteSpace="nowrap">Name</Table.ColumnHeader>
                  <Table.ColumnHeader whiteSpace="nowrap">Machine Key</Table.ColumnHeader>
                  <Table.ColumnHeader whiteSpace="nowrap">Company / Branch</Table.ColumnHeader>
                  <Table.ColumnHeader whiteSpace="nowrap">Date Time</Table.ColumnHeader>
                  <Table.ColumnHeader whiteSpace="nowrap" textAlign="right">Action</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {recentSystems
                  .slice()
                  .sort((a, b) =>
                    new Date(b.registeredAtUtc ?? b.licenseExpiryDate).getTime() -
                    new Date(a.registeredAtUtc ?? a.licenseExpiryDate).getTime()
                  )
                  .map(system => (
                    <Table.Row key={system.id}>
                      <Table.Cell>{system.systemName}</Table.Cell>
                      <Table.Cell>{system.machineKey || '—'}</Table.Cell>
                      <Table.Cell>
                        {system.branch?.company?.name || '—'} / {system.branch?.name || '—'}
                      </Table.Cell>
                      <Table.Cell>
                        {system.registeredAtUtc
                          ? new Date(system.registeredAtUtc).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh' })
                          : new Date(system.licenseExpiryDate).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh' })}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Button
                          size="xs"
                          variant="outline"
                          colorPalette="blue"
                          onClick={() =>
                            navigate(`/systems?machineKey=${encodeURIComponent(system.machineKey || '')}`)
                          }
                        >
                          Machine
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Box>

        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="white" overflow="auto" maxH="400px">
          <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100" position="sticky" top="0" bg="white" zIndex={1}>
            <Heading size="md">Expiring in 30 Days</Heading>
          </Box>
          <Box px={2} py={2} overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader whiteSpace="nowrap">Name</Table.ColumnHeader>
                  <Table.ColumnHeader whiteSpace="nowrap">Machine Key</Table.ColumnHeader>
                  <Table.ColumnHeader whiteSpace="nowrap">Company / Branch</Table.ColumnHeader>
                  <Table.ColumnHeader whiteSpace="nowrap">Date Time</Table.ColumnHeader>
                  <Table.ColumnHeader whiteSpace="nowrap" textAlign="right">Action</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {expiringSystems
                  .slice()
                  .sort((a, b) => new Date(a.licenseExpiryDate).getTime() - new Date(b.licenseExpiryDate).getTime())
                  .map(system => (
                    <Table.Row key={system.id}>
                      <Table.Cell>{system.systemName}</Table.Cell>
                      <Table.Cell>{system.machineKey || '—'}</Table.Cell>
                      <Table.Cell>
                        {system.branch?.company?.name || '—'} / {system.branch?.name || '—'}
                      </Table.Cell>
                      <Table.Cell>
                        {new Date(system.licenseExpiryDate).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh' })}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Button
                          size="xs"
                          variant="outline"
                          colorPalette="blue"
                          onClick={() =>
                            navigate(`/systems?machineKey=${encodeURIComponent(system.machineKey || '')}`)
                          }
                        >
                          Machine
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Box>
      </SimpleGrid>

      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="white" display="flex" flexDirection="column" maxH="400px">
        <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100" bg="white" flexShrink={0}>
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <HStack gap={2}>
              <Heading size="md">Last Ping (auto-refreshes every 10s)</Heading>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Refresh"
                loading={refreshLoading}
                onClick={handleRefresh}
              >
                <RefreshIcon />
              </IconButton>
            </HStack>
            <Button
              size="sm"
              variant="outline"
              colorPalette="red"
              loading={clearHistoryLoading}
              onClick={handleClearHistory}
            >
              Clear History
            </Button>
          </HStack>
        </Box>
        <Box overflow="auto" flex="1">
          <Table.Root>
            <Table.Header position="sticky" top="0" bg="white" zIndex={1}>
              <Table.Row>
                <Table.ColumnHeader px={4}>System Name</Table.ColumnHeader>
                <Table.ColumnHeader px={4}>Machine Key</Table.ColumnHeader>
                <Table.ColumnHeader px={4}>Branch</Table.ColumnHeader>
                <Table.ColumnHeader px={4}>Company</Table.ColumnHeader>
                <Table.ColumnHeader px={4}>Last Ping</Table.ColumnHeader>
                <Table.ColumnHeader px={4} textAlign="right">Action</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.entries(latestStatus)
                .sort(([, a], [, b]) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime())
                .map(([key, status]) => {
                const system = recentSystems.find(s => s.machineKey === key);
                return (
                  <Table.Row key={key}>
                    <Table.Cell px={4}>{system?.systemName || ''}</Table.Cell>
                    <Table.Cell px={4}>{key}</Table.Cell>
                    <Table.Cell px={4}>{system?.branch?.name || '—'}</Table.Cell>
                    <Table.Cell px={4}>{system?.branch?.company?.name || '—'}</Table.Cell>
                    <Table.Cell px={4}>{timeAgo(status.timeStamp)}</Table.Cell>
                    <Table.Cell px={4} textAlign="right">
                      <Button
                        size="xs"
                        variant="outline"
                        colorPalette="blue"
                        onClick={() => navigate(`/systems?machineKey=${encodeURIComponent(key)}`)}
                      >
                        Machine
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>
      </Box>
  );
}
